/**
 * stepSchema.js — what a step is allowed to say, and the check that says so.
 *
 * Steps are plain data. This file gives them their defaults and validates a
 * whole definition, so adding a step is editing one array and the suite tells
 * you if you got it wrong.
 *
 * @typedef {string|((ctx: TutorialContextValue) => string)|{selector: string, padding?: number}} TargetRef
 *   A TUTORIAL_TARGETS value, a function returning a CSS selector (for things
 *   that only exist once the tutorial knows an id, like a specific node), or an
 *   explicit selector with its own spotlight padding.
 *
 * @typedef {Object} TutorialStep
 * @prop {string} id                 unique within the tutorial
 * @prop {string} route              matchPath pattern; ':scenarioId' is filled from entities
 * @prop {string} title
 * @prop {string|((ctx) => string)} body
 * @prop {TargetRef[]} spotlight     the holes cut in the dim overlay
 * @prop {TargetRef[]} [allow]       what stays clickable; defaults to `spotlight`, [] locks everything
 * @prop {string[]} [deny]           raw selectors refused even inside an allowed area
 * @prop {number} [anchor=0]         which spotlight the tooltip points at
 * @prop {'top'|'bottom'|'left'|'right'} [placement='bottom']
 * @prop {string[]} [block]          extra event types to swallow for this step (e.g. ['wheel'])
 * @prop {string} [sidebarPanel]     editor panel to force open while this step runs
 * @prop {{x: number, y: number}} [dropAt]
 *                                   where a situation dropped during this step lands,
 *                                   as fractions of the visible canvas (0 = left/top,
 *                                   1 = right/bottom). Set it when a later step needs
 *                                   to know where the node is; omit it to let the
 *                                   author drop wherever they like.
 * @prop {Object} advanceOn
 *   {type:'next'}                                  the tooltip's Next button
 *   {type:'click', target}                         a click landing inside `target`
 *   {type:'route', path}                           the location matches `path`
 *   {type:'event', name, when?, capture?}          a tutorialBus event; `capture`
 *                                                  stores the new id under that key
 * @prop {Object<string,string|Function>} [hintOn]  event name → correction text, for
 *                                                  events that are not this step's advance
 * @prop {(ctx, actions) => void} [onEnter]
 * @prop {(payload, ctx, actions) => any} [onEvent] runs on a matching advance event; pre-fill here
 * @prop {{handle: (payload, ctx, actions) => string|void}} [invalid]
 *                                                  the event fired but `when` said no: undo and explain
 * @prop {{timeoutMs?: number}} [waitFor]
 * @prop {Partial<TutorialStep>} [mobile]           merged over the step on small screens
 */

import { TUTORIAL_TARGET_VALUES } from '../targets';

const ADVANCE_TYPES = ['next', 'click', 'route', 'event'];
const PLACEMENTS = ['top', 'bottom', 'left', 'right'];

export const DEFAULT_RESOLVE_TIMEOUT_MS = 10000;

/**
 * Applies defaults and the mobile overrides. Returns a new object; the
 * definition is never mutated.
 */
export function normalizeStep(step, { isMobile = false } = {}) {
    if (!step) return null;
    const merged = isMobile && step.mobile ? { ...step, ...step.mobile } : step;
    const spotlight = merged.spotlight || [];

    return {
        ...merged,
        spotlight,
        allow: merged.allow === undefined ? spotlight : merged.allow,
        deny: merged.deny || [],
        anchor: merged.anchor ?? 0,
        placement: merged.placement || 'bottom',
        block: merged.block || [],
        waitFor: { timeoutMs: DEFAULT_RESOLVE_TIMEOUT_MS, ...(merged.waitFor || {}) },
    };
}

/**
 * Collects every problem with a definition rather than throwing on the first, so
 * one test run tells you everything to fix.
 *
 * @returns {string[]} empty when the definition is sound
 */
export function validateTutorial(definition) {
    const problems = [];
    if (!definition || typeof definition !== 'object') return ['definition is not an object'];
    if (!definition.id) problems.push('definition is missing an id');
    if (!Number.isInteger(definition.version)) problems.push(`${definition.id}: version must be an integer`);
    if (!Array.isArray(definition.steps) || definition.steps.length === 0) {
        problems.push(`${definition.id}: steps must be a non-empty array`);
        return problems;
    }

    const seen = new Set();
    const captured = new Set();

    definition.steps.forEach((raw, index) => {
        const where = `${definition.id}[${index}] ${raw?.id || '(no id)'}`;
        const step = normalizeStep(raw);

        if (!step.id) problems.push(`${where}: missing id`);
        else if (seen.has(step.id)) problems.push(`${where}: duplicate id`);
        else seen.add(step.id);

        if (!step.route) problems.push(`${where}: missing route`);
        if (!step.title) problems.push(`${where}: missing title`);
        if (step.body === undefined || step.body === null) problems.push(`${where}: missing body`);
        if (!PLACEMENTS.includes(step.placement)) problems.push(`${where}: bad placement "${step.placement}"`);

        const checkTarget = (ref, label) => {
            if (typeof ref === 'function') return;
            if (ref && typeof ref === 'object' && typeof ref.selector === 'string') return;
            if (typeof ref === 'string' && TUTORIAL_TARGET_VALUES.includes(ref)) return;
            problems.push(`${where}: ${label} references unknown target ${JSON.stringify(ref)}`);
        };

        if (!Array.isArray(step.spotlight)) problems.push(`${where}: spotlight must be an array`);
        else step.spotlight.forEach((ref) => checkTarget(ref, 'spotlight'));

        if (!Array.isArray(step.allow)) problems.push(`${where}: allow must be an array`);
        else step.allow.forEach((ref) => checkTarget(ref, 'allow'));

        if (step.anchor < 0 || (step.spotlight.length > 0 && step.anchor >= step.spotlight.length)) {
            problems.push(`${where}: anchor ${step.anchor} is outside spotlight`);
        }

        const advance = step.advanceOn;
        if (!advance || !ADVANCE_TYPES.includes(advance.type)) {
            problems.push(`${where}: advanceOn.type must be one of ${ADVANCE_TYPES.join(', ')}`);
        } else if (advance.type === 'click') {
            if (!advance.target) problems.push(`${where}: advanceOn.click needs a target`);
            else checkTarget(advance.target, 'advanceOn.target');
        } else if (advance.type === 'route') {
            if (!advance.path) problems.push(`${where}: advanceOn.route needs a path`);
        } else if (advance.type === 'event') {
            if (!advance.name) problems.push(`${where}: advanceOn.event needs a name`);
            if (advance.when !== undefined && typeof advance.when !== 'function') {
                problems.push(`${where}: advanceOn.when must be a function`);
            }
            if (advance.capture) captured.add(advance.capture);
        }

        if (step.dropAt !== undefined) {
            const inCanvas = (n) => typeof n === 'number' && n >= 0 && n <= 1;
            if (!inCanvas(step.dropAt?.x) || !inCanvas(step.dropAt?.y)) {
                problems.push(`${where}: dropAt.x and dropAt.y must be fractions of the canvas, 0 to 1`);
            }
        }

        if (step.onEnter !== undefined && typeof step.onEnter !== 'function') {
            problems.push(`${where}: onEnter must be a function`);
        }
        if (step.onEvent !== undefined && typeof step.onEvent !== 'function') {
            problems.push(`${where}: onEvent must be a function`);
        }
        if (step.invalid !== undefined && typeof step.invalid?.handle !== 'function') {
            problems.push(`${where}: invalid.handle must be a function`);
        }
        if (step.onEvent && step.advanceOn?.type !== 'event') {
            problems.push(`${where}: onEvent only runs for an event step`);
        }
        if (step.invalid && step.advanceOn?.type !== 'event') {
            problems.push(`${where}: invalid only runs for an event step`);
        }
    });

    return problems;
}

/** Names the tutorial learns as it runs. Exposed so tests can assert on them. */
export function capturedEntityKeys(definition) {
    const keys = new Set();
    (definition?.steps || []).forEach((step) => {
        if (step?.advanceOn?.type === 'event' && step.advanceOn.capture) keys.add(step.advanceOn.capture);
    });
    return keys;
}
